import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import * as api from '../api/products';

function patchProductStockBySku(qc, sku, newStock) {
  qc.setQueriesData({ queryKey: ['products'], exact: false }, (old) => {
    if (!old) return old;
    if (Array.isArray(old)) {
      return old.map((p) => (p.sku === sku ? { ...p, stock: newStock } : p));
    }
    if (typeof old === 'object' && 'sku' in old && old.sku === sku) {
      return { ...old, stock: newStock };
    }
    return old;
  });
}

export const useProducts = (params) =>
  useQuery({
    queryKey: ['products', params],
    queryFn: () => api.getProducts(params),
  });

export const useProduct = (uuid) =>
  useQuery({
    queryKey: ['products', uuid],
    queryFn: () => api.getProduct(uuid),
    enabled: !!uuid,
  });

export const useLowStockProducts = () =>
  useQuery({
    queryKey: ['products', 'low-stock'],
    queryFn: () => api.getLowStockProducts(),
  });

export const useProductStats = () =>
  useQuery({
    queryKey: ['products', 'stats'],
    queryFn: () => api.getProductStats(),
  });

export const useProductsPaginated = (params) =>
  useInfiniteQuery({
    queryKey: ['products', 'paginated', params],
    queryFn: ({ pageParam = 1 }) =>
      api.getProductsPaginated({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    placeholderData: (prev) => prev,
  });

export const useCreateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.createProduct(data),
    onSuccess: (created) => {
      qc.setQueryData(['products', created.uuid], created);
      qc.invalidateQueries({ queryKey: ['products'], exact: false });
    },
  });
};

export const useUpdateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }) => api.updateProduct(uuid, data),
    onSuccess: (updated, { uuid }) => {
      qc.setQueryData(['products', uuid], updated);
      qc.invalidateQueries({ queryKey: ['products'], exact: false });
    },
  });
};

export const useDeleteProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid) => api.deleteProduct(uuid),
    onSuccess: (_, uuid) => {
      qc.removeQueries({ queryKey: ['products', uuid] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
};

export const useUpdateStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sku, data }) => api.updateStock(sku, data),
    onSuccess: (result) => {
      patchProductStockBySku(qc, result.sku, result.newStock);
      qc.invalidateQueries({ queryKey: ['products'], exact: false });
    },
  });
};

export const useLinkIngredient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, data }) => api.linkIngredient(uuid, data),
    onSuccess: (_, { uuid }) => {
      qc.invalidateQueries({ queryKey: ['products', uuid] });
      qc.invalidateQueries({ queryKey: ['products'], exact: false });
    },
  });
};

export const useUnlinkIngredient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ productSku, ingredientSku }) =>
      api.unlinkIngredient(productSku, ingredientSku),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
};
